import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../features/userSlice";
import { useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { discussionHandlerMap } from "../discussionLogics"; // 👈 Import from index
import { setFilesList } from "../../../features/genericCommentsAndFilesSlice";
import { readFileAsBase64 } from "../../../utils/helpers";

export const useFileUploads = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const [isDragging, setIsDragging] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]); // store files which are we selected brower or drag and drop(when create a new task)
  const [files, setFiles] = useState<any>([]); // store files which are we selected brower or drag and drop (when edit a task)
  const [seletedFilesByEditor, setSeletedFilesByEditor] = useState<any>([]);

  const moduleType = useSelector(
    (state: any) => state.genericCommentsAndFiles.moduleType
  ) as keyof typeof discussionHandlerMap;

  const objectType = useSelector(
    (state: any) => state.genericCommentsAndFiles.objectType
  );
  const shared_uid = useSelector(
    (state: any) => state.genericCommentsAndFiles.objectSharedUid
  );

  const object_id = useSelector(
    (state: any) => state.genericCommentsAndFiles.commentObjectID
  ); // when try adding new file in All Forecast Tab

  const multiMetaSharedUid = useSelector(
    (state: any) => state.genericCommentsAndFiles.multiMetaShearedUid
  );

  // Convert shared_uid to an array if it's a single string(when fetching comments for single / multiple object_shared_uid)
  const object_shared_uid: string[] = useMemo(() => {
    if (Array.isArray(multiMetaSharedUid) && multiMetaSharedUid.length > 0) {
      return multiMetaSharedUid; // AllData tab
    }
    return shared_uid ? [shared_uid] : []; // SingleData tab
  }, [shared_uid, multiMetaSharedUid]);

  const handlers = discussionHandlerMap[moduleType]; // Get the handlers for the module type like ActionItem/BusinessInsight

  //-- this for when selected files
  const handleFileUpload = (event: any) => {
    const selectedFiles = Array.from(event.target.files) as File[];
    processFiles(selectedFiles, "inputBox");
  };

  //-- this for remove files when clicking on cross icon
  const handleRemoveFile = async (fileId: string) => {
    if (!handlers?.deleteFile) return; // prevent call if handler is undefined
    try {
      await handlers.deleteFile(oauthTokenHeader, fileId);
      setFiles((prevFiles: any[]) =>
        prevFiles.filter((file: any) => file.shared_uid !== fileId)
      );

      const updatedFiles = files.filter(
        (file: any) => file.shared_uid !== fileId
      );
      dispatch(setFilesList(updatedFiles));

      await handlers?.fetchFiles(
        oauthTokenHeader,
        objectType,
        object_shared_uid,
        (fetchedFiles: any) => {
          setFiles(fetchedFiles); //  Existing logic (you don’t want to remove)
          //  NEW: Also store in Redux
          dispatch(setFilesList(fetchedFiles));
        }
      );
    } catch (error: any) {
      dispatch({
        type: "error",
        payload: { message: error?.parsedText || "Unknown error occurred" },
      });
    }
  };

  //-- this for drag and drop
  const handleFileDrop = (event: any) => {
    event.preventDefault();
    setIsDragging(false);

    // const newFiles = Array.from(event.dataTransfer.files) as File[];
    // processFiles(newFiles, "inputBox");
  };

  // -- this for set Files(name, size) in a state(setUploadedFiles), validation, convert file to base64
  const processFiles = async (newFiles: File[], selectedFrom: string) => {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    const validFiles: any[] = [];

    for (const file of newFiles) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 5.0) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `File size exceeds the 5MB limit.`,
            type: "error",
          })
        );
        continue;
      }

      if (!allowedTypes.includes(file.type)) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `Invalid file type: ${file.name}. Only PDF, JPG, and PNG allowed.`,
            type: "error",
          })
        );
        continue;
      }

      const base64String = await readFileAsBase64(file);
      validFiles.push({
        file: file.name,
        size: file.size,
        base64: base64String,
        progress: 100,
        status: "Uploaded",
      });
    }

    if (selectedFrom === "inputBox") {
      setUploadedFiles((prev: any) => [...prev, ...validFiles]);
    } else if (selectedFrom === "fromEditor") {
      setSeletedFilesByEditor((prev: any) => [...prev, ...validFiles]);
    }
  };

  // -- MAKE API call for Fetch Files
  const fetchFilesThisModule = async () => {
    setFileUploadLoading(true); // Set loading to true before API call
    try {
      await handlers?.fetchFiles(
        oauthTokenHeader,
        objectType,
        object_shared_uid,
        (fetchedFiles: any) => {
          setFiles(fetchedFiles); //  Existing logic (you don’t want to remove)

          //  NEW: Also store in Redux
          dispatch(setFilesList(fetchedFiles));
        }
      );
      //setSelectedFiles([]);
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      setFileUploadLoading(false); // Set false after API finishes
    }
  };

  //-- MAKE API call for Files upload
  const postFiles = async (task_shared_uid: string, selectedFiles: any[]) => {
    setFileUploadLoading(true); // Set false after API finishes
    try {
      const uploadPromises = selectedFiles?.map(async (file: any) => {
        const fileUploadBody = {
          object_type: objectType,
          file_name: file.file, // Extract file name
          file: file.base64, // File in Base64 format
        } as object;

        return handlers?.uploadFiles(
          oauthTokenHeader,
          fileUploadBody,
          task_shared_uid
        );
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      setSeletedFilesByEditor([]);
      setUploadedFiles([]); // Reset uploaded files state
      await fetchFilesThisModule(); // fetch files from API
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      // Reset states after upload
      setFileUploadLoading(false); // Set false after API finishes
    }
  };

  useEffect(() => {
    if (uploadedFiles?.length === 0) {
      return; // Prevent unnecessary execution
    }
    postFiles(shared_uid, uploadedFiles);
    // eslint-disable-next-line
  }, [uploadedFiles]);

  //this for adding new files from the tiptap editor
  useEffect(() => {
    if (seletedFilesByEditor?.length === 0) return; // Prevent unnecessary execution
    let specific_shared_uid = shared_uid ? shared_uid : object_id;
    postFiles(specific_shared_uid, seletedFilesByEditor);
    // eslint-disable-next-line
  }, [seletedFilesByEditor]);

  //-- Fetch files (if present)
  useEffect(() => {
    if (!object_shared_uid?.length || !objectType || !moduleType) return;

    const timeout = setTimeout(() => {
      fetchFilesThisModule();
    }, 200);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, [object_shared_uid, objectType, moduleType]);

  return {
    handleFileUpload,
    handleRemoveFile,
    uploadedFiles,
    setUploadedFiles,
    handleFileDrop,
    isDragging,
    setIsDragging,
    fileUploadLoading,
    setFileUploadLoading,
    fetchFilesThisModule,
    files,
    setFiles,
    postFiles,
    processFiles,
  };
};
